import {
  type ConsultationProps,
  type ConsultationStatus,
} from '@/features/jobs/consultations/Consultation'
import type { TimeRange } from '@/infra/time-range'
import { safeParseDate } from '@/utils/date-util'
import {
  durationToMinutes,
  maybeParseIntervalToTimeRange,
  timestampToISO,
} from '@/utils/grpc-util'
import { isNotNil } from '@/utils/object-util'
import { Consultation as ConsultationProto } from '@taxfyle/api-internal/internal/consultation_pb'
import type { PrimeUserFn } from '@/features/iam'

/**
 * Maps a proto consultation to a consultation.
 *
 * @param proto The proto consultation to map.
 * @returns The consultation.
 */
export function mapConsultationProtoToConsultation(
  proto: ConsultationProto,
  primeUser: PrimeUserFn,
): ConsultationProps {
  return {
    id: proto.getId(),
    jobId: proto.getJobId(),
    skipScheduling: proto.getSkipScheduling(),
    clientUserPublicId: proto.getClientUserPublicId(),
    createdByUserPublicId: proto.getCreatedByUserPublicId(),
    createTime: safeParseDate(timestampToISO(proto.getCreateTime())),
    status: mapConsultationStatusProtoToStatus(proto.getStatus()),
    requestedLength: proto.getRequestedLength()
      ? durationToMinutes(proto.getRequestedLength()!)
      : 0,
    availability: mapAvailabilityProtoToTimeRangeList(proto.getAvailability()),
    availabilityCreateTime: safeParseDate(
      timestampToISO(proto.getAvailability()?.getCreateTime()),
    ),
    bookedTime: maybeParseIntervalToTimeRange(proto.getBookedTime()) ?? null,
    bookingCreateTime: safeParseDate(
      timestampToISO(proto.getBookingCreateTime()),
    ),
    providerUser: proto.getProviderUserPublicId()
      ? primeUser({ id: proto.getProviderUserPublicId() })
      : null,
    roomId: proto.getRoomId(),
  }
}

/**
 * Maps a proto availability to a list of time ranges.
 *
 * @param proto The proto availability to map.
 * @returns The list of time ranges.
 */
function mapAvailabilityProtoToTimeRangeList(
  proto?: ConsultationProto.Availability,
): TimeRange[] {
  return (
    proto
      ?.getAvailabilityTimesList()
      .map(maybeParseIntervalToTimeRange)
      .filter(isNotNil) ?? []
  )
}

/**
 * Maps a proto consultation status to a consultation status.
 * @param proto The proto consultation status to map.
 * @returns The consultation status.
 */
export function mapConsultationStatusProtoToStatus(
  proto: ConsultationProto.ConsultationStatus,
): ConsultationStatus {
  switch (proto) {
    case ConsultationProto.ConsultationStatus.PENDING_AVAILABILITY:
      return 'PendingAvailability'
    case ConsultationProto.ConsultationStatus.PENDING_BOOKING:
      return 'PendingBooking'
    case ConsultationProto.ConsultationStatus.PENDING_CALL:
      return 'PendingCall'
    case ConsultationProto.ConsultationStatus.IN_PROGRESS:
      return 'InProgress'
    case ConsultationProto.ConsultationStatus.DONE:
      return 'Done'
    case ConsultationProto.ConsultationStatus.CANCELLED:
      return 'Cancelled'
    default:
      return 'Unknown'
  }
}
